/**
 * Adapters for the shared <SkillEditor scope> component (PR-D).
 *
 * Each adapter implements `SkillEditorAdapter` from `./SkillEditor.types`
 * — its job is to smooth over the scope-conditional data hooks, save
 * payload shapes, affordance flags, and copy strings so the shared body
 * sees ONE normalized contract regardless of scope.
 *
 * See SkillEditor.tsx for the canonical scope-deviation table.
 */
import { BookOpen } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  useStudioSkills,
  useCreateStudioSkill,
  useUpdateStudioSkill,
  useDeleteStudioSkill,
  useCloneStudioSkillFromSystem,
  type StudioSkill,
  type StudioSkillVariable,
} from '@/lib/use-studio-skills';
import {
  useSysAdminSystemSkills,
  useCreateSystemSkill,
  useDeleteSystemSkill,
  type SystemSkill,
} from '@/lib/use-sysadmin-system-catalog';
import {
  EMPTY_DRAFT,
  EMPTY_SYSADMIN_DRAFT,
  buildTenantCreateBody,
  buildTenantUpdateBody,
  buildSysadminCreateBody,
  buildSysadminUpdateBody,
} from './SkillEditor.helpers';
import type {
  ListResult,
  MutationFn,
  NormalizedSkill,
  SaveSkillArgs,
  SerializeResult,
  SkillDraft,
  SkillEditorAdapter,
  SkillEditorScope,
} from './SkillEditor.types';

// ─── Row normalisers ────────────────────────────────────────────────────────

function normalizeTenantRow(s: StudioSkill): NormalizedSkill {
  return {
    id: s.id,
    slug: s.name,
    displayName: s.displayName,
    description: s.description,
    instructions: s.instructions,
    toolDependencies: s.toolDependencies,
    variables: s.variables,
    examples: [], // tenant rows don't carry examples
    category: s.category,
    isActive: s.isActive,
    scope: s.scope,
    clonedFromSystemId: s.clonedFromSystemId ?? null,
    version: null,
    updatedAt: s.updatedAt,
  };
}

function normalizeSysadminRow(s: SystemSkill): NormalizedSkill {
  return {
    id: s.id,
    slug: s.slug,
    displayName: s.displayName,
    description: s.description,
    instructions: s.instructions,
    toolDependencies: s.toolDependencies,
    // SystemSkill.variables comes typed as SysVariable[]; cast to the
    // StudioSkillVariable shape (same wire format — name/type/desc/req).
    variables: s.variables as unknown as StudioSkillVariable[],
    examples: s.examples,
    category: s.category,
    isActive: s.isActive,
    scope: 'system',
    clonedFromSystemId: null,
    version: s.version,
    updatedAt: s.updatedAt,
  };
}

// ─── Sysadmin id-in-payload update mutation ────────────────────────────────
//
// The base hook in `use-sysadmin-system-catalog.ts` is
// `useUpdateSystemSkill(id: string)` — id-bound at hook-call time. The
// shared body discovers the id at save-time, so we re-implement the
// PATCH mutation here with an id-in-payload pattern to keep the save()
// contract scope-agnostic. The semantics match the pre-refactor
// `useUpdateSystemSkill(editing?.id ?? '')` exactly: same endpoint,
// same body, same query invalidation keys.

const SYSADMIN_BASE = '/admin/platform/system-catalog';

function useUpdateSystemSkillById() {
  const qc = useQueryClient();
  return useMutation<
    { data: SystemSkill },
    Error,
    { id: string; body: Partial<SystemSkill> }
  >({
    mutationFn: ({ id, body }) =>
      apiClient.request<{ data: SystemSkill }>(
        `${SYSADMIN_BASE}/skills/${id}`,
        {
          method: 'PATCH',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
        },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ['sysadmin-system-catalog', 'skills'],
      });
      qc.invalidateQueries({
        queryKey: ['sysadmin-system-catalog', 'skill', vars.id],
      });
    },
  });
}

// ─── Tenant adapter ─────────────────────────────────────────────────────────

export const tenantAdapter: SkillEditorAdapter = Object.freeze({
  // ─── Page chrome ─────────────────────────────────────────────────────
  pageTitle: 'Skills',
  pageSubtitle:
    "Composable instruction sets you attach to your custom agents. Skills are purely yours — they don't run inside the platform's Argus model wrapper chain.",
  newSheetTitle: 'New skill',
  sheetDescription:
    'Define a new composable instruction set for your custom agents.',
  emptyStateMessage:
    'No skills yet. Create your first skill with the button above.',
  loadingMessage: 'Loading skills…',
  deleteDialogTitle: 'Delete skill',
  deleteConfirmMessage:
    "Any agents that referenced this skill will lose the attachment; they keep working but the skill's instructions are no longer applied.",
  deleteActionLabel: 'Delete skill',
  deleteSuccessTemplate: 'Deleted "${displayName}"',

  // ─── Affordance flags (Bucket B) ─────────────────────────────────────
  showScopeChips: true,
  showCategoryDropdown: true,
  showRowCount: true,
  showToolsColumn: false,
  showVersionColumn: false,
  showVariablesColumn: true,
  showUpdatedColumn: true,
  showSourceBadges: true,
  showSystemRowActions: true,
  showHelperPanel: true,
  showProvenanceBanner: true,
  showVariableEditor: true,
  showToolDependenciesPicker: true,
  showToolDependenciesText: false,
  showVariablesText: false,
  showExamplesText: false,
  activeSwitchPlacement: 'bordered-row',
  clickableTableRows: false,
  showCardDescriptionRow: false,

  // ─── Layout / icon flags ─────────────────────────────────────────────
  HeadingIcon: BookOpen,
  newButtonLabel: 'New Skill',

  // ─── Draft factories ─────────────────────────────────────────────────
  emptyDraft(): SkillDraft {
    return { ...EMPTY_DRAFT };
  },
  draftFromRow(s: NormalizedSkill): SkillDraft {
    return {
      id: s.id,
      slug: s.slug,
      displayName: s.displayName,
      description: s.description,
      instructions: s.instructions,
      toolDependencies: s.toolDependencies,
      variables: s.variables,
      category: s.category,
      isActive: s.isActive,
      clonedFromSystemId: s.clonedFromSystemId,
    };
  },

  // ─── Placeholder strings ─────────────────────────────────────────────
  slugPlaceholder: 'my-skill',
  displayNamePlaceholder: 'My Skill',
  descriptionPlaceholder:
    "What this skill does and when an agent should use it.",
  instructionsPlaceholder:
    'Step-by-step instructions the agent will follow when this skill is attached. Reference variables as {{name}}.',
  categoryPlaceholder: 'general',

  // ─── Data hooks ──────────────────────────────────────────────────────
  useList(): ListResult {
    // Tenant view merges system + tenant rows in a single list (Studio
    // Phase 3E behaviour). The body renders Clone for system rows and
    // Edit for tenant rows via the source-column affordances.
    const { data, isLoading, error } = useStudioSkills({ scope: 'all' });
    return {
      skills: (data?.items ?? []).map(normalizeTenantRow),
      loading: isLoading,
      error: error ?? null,
    };
  },

  // ─── Save (create OR update) ─────────────────────────────────────────
  useSave() {
    const createSkill = useCreateStudioSkill();
    const updateSkill = useUpdateStudioSkill();
    return {
      saveInFlight: createSkill.isPending || updateSkill.isPending,
      save({ body, isEdit, id, onSuccess, onError }: SaveSkillArgs) {
        if (isEdit) {
          if (!id) {
            onError(new Error('Internal: update called without id'));
            return;
          }
          updateSkill.mutate(
            { id, ...(body as Record<string, unknown>) } as Parameters<
              typeof updateSkill.mutate
            >[0],
            {
              onSuccess: () => onSuccess(),
              onError,
            },
          );
        } else {
          createSkill.mutate(
            body as Parameters<typeof createSkill.mutate>[0],
            {
              onSuccess: () => onSuccess(),
              onError,
            },
          );
        }
      },
    };
  },

  useDelete(): MutationFn {
    const del = useDeleteStudioSkill();
    return {
      inFlight: del.isPending,
      run({ id, onSuccess, onError }) {
        del.mutate({ id }, { onSuccess: () => onSuccess(), onError });
      },
    };
  },

  useCloneFromSystem(): MutationFn | null {
    const clone = useCloneStudioSkillFromSystem();
    return {
      inFlight: clone.isPending,
      run({ id, onSuccess, onError }) {
        clone.mutate(
          { systemId: id },
          { onSuccess: (resp) => onSuccess(resp), onError },
        );
      },
    };
  },

  // ─── Payload serializers (Bucket C) ──────────────────────────────────
  serializeCreatePayload(draft: SkillDraft): SerializeResult {
    return { ok: true, body: buildTenantCreateBody(draft) };
  },
  serializeUpdatePayload(draft: SkillDraft): SerializeResult {
    return { ok: true, body: buildTenantUpdateBody(draft) };
  },
});

// ─── Sysadmin adapter ───────────────────────────────────────────────────────

export const sysadminAdapter: SkillEditorAdapter = Object.freeze({
  // ─── Page chrome ─────────────────────────────────────────────────────
  pageTitle: 'System Skills',
  pageSubtitle:
    'Sysadmin-authored skills published to every tenant. Tenants can clone a system skill into their own list (Q3) and edit the clone.',
  newSheetTitle: 'New system skill',
  sheetDescription:
    "Slug is the manifest upsert key. Markdown instructions render in the tenant's skill picker on clone.",
  emptyStateMessage: 'No system skills yet.',
  loadingMessage: 'Loading…',
  deleteDialogTitle: 'Deactivate system skill',
  deleteConfirmMessage:
    'Tenants stop seeing it. Existing tenant clones (cloned_from_system_id) are unaffected.',
  deleteActionLabel: 'Deactivate',
  deleteSuccessTemplate: 'Skill "${slug}" deactivated',

  // ─── Affordance flags (Bucket B) ─────────────────────────────────────
  showScopeChips: false,
  showCategoryDropdown: false,
  showRowCount: false,
  showToolsColumn: true,
  showVersionColumn: true,
  showVariablesColumn: false,
  showUpdatedColumn: false,
  showSourceBadges: false,
  showSystemRowActions: false,
  showHelperPanel: false,
  showProvenanceBanner: false,
  showVariableEditor: false,
  showToolDependenciesPicker: false,
  showToolDependenciesText: true,
  showVariablesText: true,
  showExamplesText: true,
  activeSwitchPlacement: 'inline-with-category',
  clickableTableRows: true,
  showCardDescriptionRow: false,

  // ─── Layout / icon flags ─────────────────────────────────────────────
  HeadingIcon: BookOpen,
  newButtonLabel: 'New skill',

  // ─── Draft factories ─────────────────────────────────────────────────
  emptyDraft(): SkillDraft {
    return { ...EMPTY_SYSADMIN_DRAFT };
  },
  draftFromRow(s: NormalizedSkill): SkillDraft {
    return {
      id: s.id,
      slug: s.slug,
      displayName: s.displayName,
      description: s.description,
      instructions: s.instructions,
      toolDependencies: s.toolDependencies,
      variables: s.variables,
      category: s.category,
      isActive: s.isActive,
      toolDependenciesText: JSON.stringify(s.toolDependencies ?? [], null, 2),
      variablesText: JSON.stringify(s.variables ?? [], null, 2),
      examplesText: JSON.stringify(s.examples ?? [], null, 2),
      clonedFromSystemId: null,
    };
  },

  // ─── Placeholder strings ─────────────────────────────────────────────
  slugPlaceholder: 'system.maintenance_analysis',
  displayNamePlaceholder: '',
  descriptionPlaceholder: '',
  instructionsPlaceholder: '',
  categoryPlaceholder: '',

  // ─── Data hooks ──────────────────────────────────────────────────────
  useList(): ListResult {
    const { data, isLoading, error } = useSysAdminSystemSkills();
    return {
      skills: (data?.items ?? []).map(normalizeSysadminRow),
      loading: isLoading,
      error: error ?? null,
    };
  },

  // ─── Save (create OR update) ─────────────────────────────────────────
  useSave() {
    const create = useCreateSystemSkill();
    const update = useUpdateSystemSkillById();
    return {
      saveInFlight: create.isPending || update.isPending,
      save({ body, isEdit, id, onSuccess, onError }: SaveSkillArgs) {
        if (isEdit) {
          if (!id) {
            onError(new Error('Internal: update called without id'));
            return;
          }
          update.mutate(
            { id, body: body as Partial<SystemSkill> },
            {
              onSuccess: () => onSuccess(),
              onError,
            },
          );
        } else {
          create.mutate(body as Partial<SystemSkill>, {
            onSuccess: () => onSuccess(),
            onError,
          });
        }
      },
    };
  },

  useDelete(): MutationFn {
    const del = useDeleteSystemSkill();
    return {
      inFlight: del.isPending,
      run({ id, onSuccess, onError }) {
        del.mutate({ id }, { onSuccess: () => onSuccess(), onError });
      },
    };
  },

  useCloneFromSystem(): MutationFn | null {
    // Sysadmin authors originals — never clones from itself.
    return null;
  },

  // ─── Payload serializers (Bucket C) ──────────────────────────────────
  serializeCreatePayload(draft: SkillDraft): SerializeResult {
    return buildSysadminCreateBody(draft);
  },
  serializeUpdatePayload(draft: SkillDraft): SerializeResult {
    return buildSysadminUpdateBody(draft);
  },
});

// ─── Adapter selector ───────────────────────────────────────────────────────

export function getAdapter(scope: SkillEditorScope): SkillEditorAdapter {
  return scope === 'sysadmin' ? sysadminAdapter : tenantAdapter;
}
